/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography, Icon } from '@zapier/design-system';
import { ServiceIcon } from '../ServiceIcon';

export type Step = {
  icon: string;
  id: string;
  label: string;
  name: string;
};

type Props = {
  /**
   * The steps which comprise this Zap template.
   *
   * - `icon` is a URL representing an icon for the service used in this step.
   * - `id` is the ID of the service, e.g. "GoogleFormsAPI".
   * - `label` is the proper name of the trigger/action/search, e.g., "New Response in Spreadsheet".
   * - `name` is the name of the service, e.g., "Google Forms".
   * - `slug` is the service's slug, e.g., "google-forms".
   */
  steps: Array<Step>;
};

const Styles = labelStyles('ZapCardSteps', {
  root: css`
    border: 1px solid ${Colors.grey200};
    border-radius: 10px;
  `,
  step: css`
    position: relative;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 10px;
    align-items: center;
    padding: 10px;
    ${Typography.altText800};
    color: ${Colors.grey700};
  `,
  stepName: css`
    ${Typography.altText900};
    font-weight: 600;
    color: ${Colors.grey900};
  `,
  divider: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    align-items: center;

    &::before,
    &::after {
      content: '';
      height: 1px;
      background-color: ${Colors.grey200};
      flex: 1;
    }

    &::before {
      margin-right: 2px;
    }

    &::after {
      margin-left: 2px;
    }
  `,
});

type ZapCardStepProps = Step & {
  index: number;
};

const ZapCardStep = (props: ZapCardStepProps) => {
  const isTrigger = props.index === 0;
  const divider = !isTrigger && (
    <div css={Styles.divider}>
      <Icon color="grey200" name="formAddCircle" size={18} />
    </div>
  );
  return (
    <li css={Styles.step}>
      {divider}
      <ServiceIcon
        ariaHidden={true}
        serviceName={props.name}
        size={60}
        src={props.icon}
      />
      <div>
        <p>{isTrigger ? 'When this happens' : 'Then do this'}</p>
        <p css={Styles.stepName}>
          Step {props.index + 1}: {props.label}
        </p>
      </div>
    </li>
  );
};

/**
 * List of steps (e.g. trigger and actions) for a Zap.
 */
export const ZapCardSteps = (props: Props) => {
  return (
    <ol css={Styles.root}>
      {props.steps.map((step, i) => {
        // `key` uses `i` since multiple `steps` might be the same.
        return <ZapCardStep {...step} index={i} key={i} />;
      })}
    </ol>
  );
};
